/** CSS contract: css-library/components/03-layouts/modal.css. */
import type { Ref } from 'react';
import { Dialog } from '@base-ui/react/dialog';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { HTMLAttributes, ReactNode } from 'react';
import { cx } from '../lib/cx';
import { mergeRefs } from '../lib/merge-refs';
import { ModalFrame } from '../lib/modalContext';
import { retainNativeModalIsolation } from '../lib/modalIsolation';

export type ModalSize = 'compact' | 'form' | 'document';

export interface ModalProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onClose'> {
  onClose?: () => void;
  size?: ModalSize;
  presentation?: 'center' | 'side';
  busy?: boolean;
  label?: string;
  role?: 'dialog' | 'alertdialog';
  topbar?: ReactNode;
  overlayClassName?: string;
}

export function Modal(
  { ref, onClose, size = 'form', presentation = 'center', busy = false, label = 'Dialog', role = 'dialog', topbar, overlayClassName, className, children, ...rest }: ModalProps & { ref?: Ref<HTMLDivElement> },
) {
  const [presentationState, setPresentation] = useState<{ size?: ModalSize; label?: string }>({});
  const closeHandler = useRef<{ close: () => void; disabled: boolean } | null>(null);
  const registerClose = useCallback((close: () => void, disabled: boolean, headerSize?: ModalSize, headerLabel?: string) => {
    const handler = { close, disabled };
    closeHandler.current = handler;
    setPresentation((previous) => previous.size === headerSize && previous.label === headerLabel ? previous : { size: headerSize, label: headerLabel });
    return () => { if (closeHandler.current === handler) { closeHandler.current = null; setPresentation({}); } };
  }, []);
  const popovers = useRef(new Map<symbol, () => void>());
  const registerPopover = useCallback((close: () => void) => {
    const key = Symbol();
    popovers.current.set(key, close);
    return () => { popovers.current.delete(key); };
  }, []);
  const popup = useRef<HTMLDivElement>(null);
  const returnTarget = useRef<HTMLElement | null>(null);
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    const card = popup.current;
    if (!card || presentation === 'side') return;
    return retainNativeModalIsolation(card.ownerDocument);
  }, [portalContainer, presentation]);
  useLayoutEffect(() => {
    const active = document.activeElement;
    returnTarget.current = active instanceof HTMLElement ? active : null;
  }, []);
  useLayoutEffect(() => {
    const card = popup.current;
    if (busy && card && (card.contains(document.activeElement) || document.activeElement === document.body)) card.focus({ preventScroll: true });
  }, [busy]);
  useLayoutEffect(() => {
    const card = popup.current;
    if (!card || typeof ResizeObserver === 'undefined') return;
    let bar: HTMLElement | null = null;
    const measure = () => {
      if (bar) card.style.setProperty('--modal-bar-height', `${bar.offsetHeight}px`);
      else card.style.removeProperty('--modal-bar-height');
    };
    const observer = new ResizeObserver(measure);
    const track = () => {
      const next = card.querySelector<HTMLElement>(':scope > .bar');
      if (next === bar) return;
      if (bar) observer.unobserve(bar);
      bar = next;
      if (bar) observer.observe(bar);
      measure();
    };
    track();
    const changes = new MutationObserver(track);
    changes.observe(card, { childList: true });
    return () => { observer.disconnect(); changes.disconnect(); };
  }, [portalContainer]);
  useLayoutEffect(() => {
    const card = popup.current;
    if (presentation !== 'side' || !card) return;
    const main = document.querySelector<HTMLElement>('[data-main-layout]');
    const header = main?.querySelector<HTMLElement>(':scope > .bar');
    const column = main?.querySelector<HTMLElement>('.list-scroll .list-column');
    const measure = () => {
      const row = main?.querySelector<HTMLElement>('.list-scroll .list-row');
      const edge = (row ?? column)?.getBoundingClientRect().right;
      card.style.setProperty('--side-top', `${(header?.getBoundingClientRect().bottom ?? 56) + 12}px`);
      card.style.setProperty('--side-left', `${window.innerWidth <= 720 ? 12 : (edge ?? window.innerWidth * .4) + 12}px`);
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    for (const element of [main, header, column]) if (element) observer?.observe(element);
    window.addEventListener('resize', measure);
    return () => { observer?.disconnect(); window.removeEventListener('resize', measure); };
  }, [presentation, portalContainer]);
  const frame = useMemo(() => ({ portalContainer, busy, registerClose, registerPopover }), [portalContainer, busy, registerClose, registerPopover]);
  return (
    <Dialog.Root
      open
      modal={presentation === 'center'}
      disablePointerDismissal
      onOpenChange={(next, details) => {
        if (next) return;
        if (details.reason === 'escape-key' && popovers.current.size) {
          details.cancel();
          details.event.preventDefault();
          details.event.stopPropagation();
          if (details.event instanceof KeyboardEvent) details.event.stopImmediatePropagation();
          [...popovers.current.values()].at(-1)?.();
          return;
        }
        const handler = closeHandler.current;
        if (busy || handler?.disabled || (!handler && !onClose)) { details.cancel(); return; }
        (handler?.close ?? onClose)?.();
      }}
    >
      <Dialog.Portal className="modal-portal">
        {presentation === 'center' && <Dialog.Backdrop className="modal-backdrop" />}
        <Dialog.Viewport className={cx('modal-viewport', presentation === 'side' && 'modal-viewport--side', overlayClassName)} onPointerDown={(event) => {
          if (presentation === 'center' && event.target === event.currentTarget) event.preventDefault();
        }}>
          <Dialog.Popup
            ref={mergeRefs(popup, ref)}
            className={cx('layout', 'modal-card', `modal-card--${presentationState.size ?? size}`, presentation === 'side' && 'modal-card--side', className)}
            role={role}
            aria-modal={presentation === 'center' ? true : undefined}
            aria-label={rest['aria-labelledby'] ? undefined : (rest['aria-label'] ?? presentationState.label ?? label)}
            initialFocus={() => popup.current?.querySelector<HTMLElement>('input:not([disabled]), textarea:not([disabled]), [data-initial-focus]') ?? popup.current}
            finalFocus={() => {
              let target = returnTarget.current;
              if (!target?.isConnected) {
                target = document.querySelector<HTMLElement>('main h1, #page h1, h1');
                if (target && !target.hasAttribute('tabindex')) target.tabIndex = -1;
              }

              queueMicrotask(() => target?.focus({ preventScroll: true }));
              return false;
            }}
            {...rest}
          >
            <ModalFrame.Provider value={frame}>
              {topbar}
              {children}
              <div className="modal-popovers" ref={setPortalContainer} />
            </ModalFrame.Provider>
          </Dialog.Popup>
        </Dialog.Viewport>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
